import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  Output,
  Renderer2,
} from '@angular/core';
import { ButtonComponent } from 'src/app/shared/components/button/button.component';
import { HeadingComponent } from 'src/app/shared/components/heading/heading.component';
import { HeadingColor, HeadingStyle, HeadingType } from 'src/app/shared/components/heading/heading.types';
import { SessionDismissibleButtonDirective } from 'src/app/shared/directives/session-dismissible-button.directive';
import { Require } from '../component.util';

/**
 * BannerComponent displays a customizable banner, callout, or tip with optional heading, icon, and dismiss functionality.
 * All banner categories support collapsed and card verions. You can pass a class="collapsed" to the banner to show the collapsed version.
 */
@Require<BannerComponent>(['category'])
@Component({
  selector: 'mable-banner',
  standalone: true,
  imports: [CommonModule, ButtonComponent, HeadingComponent, SessionDismissibleButtonDirective],
  templateUrl: './banner.component.html',
  styleUrls: ['./banner.component.scss'],
})
export class BannerComponent implements AfterViewInit {
  /**
   * The category of the banner, which determines its visual style.
   * Options include 'banner', 'headed-banner', 'callout', 'headed-callout',
   * 'tip', 'headed-tip', 'generic', 'headed-generic', and 'headed-slim'.
   * Default is 'generic'.
   */
  @Input() category:
    | 'banner'
    | 'headed-banner'
    | 'callout'
    | 'headed-callout'
    | 'generic'
    | 'headed-generic'
    | 'headed-slim' = 'generic';

  // The icon to display in the banner heading
  @Input() icon: string;

  // The color of the icon in the banner heading
  @Input() iconColor = '';

  // The heading text for the banner
  @Input() heading: string;

  // Only used by generic category - Used for heading and generic tile border color.
  @Input() headingColor: HeadingColor = 'grey-500';

  // Unique identifier for the banner, used for dismissible functionality
  @Input() bannerId = '';

  // Add template classes to host. Workaround for hostBinding class reset.
  @Input() class;

  /**
   * Tile color.
   * DS colors are white, grey-100 and lilac-50
   */
  @Input() color = '';

  // Heading type/size (e.g., 'h3').
  @Input() headingType: HeadingType = 'h3';

  // Heading style to show heading like other headings.
  @Input() headingStyle: HeadingStyle;

  /**
   * Whether the banner can be closed.
   * If true, the banner can be closed by the user.
   * If false, the banner will not have a close button.
   * Default is true, meaning the close button is shown.
   */
  @Input() canClose = true;

  /**
   * Emits when the banner is closed.
   *
   * You can listen to this event to perform cleanup or update UI state
   * after the user closes the banner.
   */
  @Output() close: EventEmitter<void> = new EventEmitter<void>();

  // Host binding to apply dynamic classes based on the banner category and color.
  @HostBinding('class') get bannerColor() {
    let appliedColor = '';
    switch (this.bannerCategory) {
      case 'callout':
        appliedColor = this.color || 'grey-50';
        break;
      case 'banner':
      case 'slim':
        appliedColor = this.color || 'lilac-50';
        break;
      case 'generic':
      default:
        appliedColor = 'white';
    }
    return `ion-color-${appliedColor} ${this.class ? this.class : ''}`;
  }

  // Getter to determine the heading color based on the banner category.
  get bannerHeadingColor() {
    let appliedColor = this.headingColor;
    switch (this.bannerCategory) {
      case 'callout':
      case 'banner':
      case 'slim':
        appliedColor = 'grey-500';
        break;
      default:
    }
    return appliedColor;
  }

  // Getter to determine the banner category without the prefix.
  get bannerCategory() {
    return this.category.substring(this.category.lastIndexOf('-') + 1);
  }

  constructor(
    private elementRef: ElementRef,
    private renderer: Renderer2,
  ) {}

  ngAfterViewInit(): void {
    if (this.bannerCategory === 'generic') {
      this.elementRef.nativeElement.style.setProperty('--borderColor', `var(--ion-color-${this.bannerHeadingColor})`);
    }
  }

  // Method to handle the close button click event.
  onClose(event: MouseEvent): void {
    event.stopPropagation();
    this.close.emit();
  }

  /**
   * Toggles the display of the banner.
   *
   * @param display - Boolean indicating whether to show or hide the banner.
   */
  toggleTile(display = false): void {
    this.renderer.setStyle(this.elementRef.nativeElement, 'display', display ? 'flex-flow' : 'none');
  }
}
